import { useState, useEffect } from 'react';
import { Text, BlockStack, InlineStack, Icon, Spinner, Banner } from '@shopify/polaris';
import { CheckIcon, XIcon } from '@shopify/polaris-icons';
import { Layout } from '../components/Layout';
import { billingApi, type PlanData, type SubscriptionData, type BillingInterval } from '../lib/api';
import { useSearchParams } from 'react-router-dom';

interface PricingPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  description: string;
  features: { name: string; included: boolean }[];
  isCurrent: boolean;
  isPopular?: boolean;
  color: string;
}

// Helper to build plan features from API data
function buildPlanFeatures(plan: PlanData): { name: string; included: boolean }[] {
  const features = plan.features;

  // Common features that appear for all plans
  const baseFeatures = [
    { key: 'sites', name: getSitesText(plan.max_sites), included: true },
    { key: 'data_retention', name: 'Unlimited data retention', included: true },
    { key: 'standard_reports', name: 'Standard reports', included: true },
    { key: 'basic_charts', name: 'Basic charts', included: features.includes('basic_charts') },
  ];

  // Features based on plan tier - all plans show 11 features for balanced card heights
  if (plan.name === 'free') {
    return [
      ...baseFeatures,
      { key: 'traffic_sources', name: 'Traffic sources', included: false },
      { key: 'ecommerce_reports', name: 'E-commerce reports', included: false },
      { key: 'geographic_data', name: 'Geographic data', included: false },
      { key: 'pdf_export', name: 'PDF export', included: false },
      { key: 'realtime_analytics', name: 'Realtime analytics', included: false },
      { key: 'all_charts', name: 'All charts', included: false },
      { key: 'hourly_heatmap', name: 'Hourly heatmap', included: false },
    ];
  }

  if (plan.name === 'starter') {
    return [
      ...baseFeatures,
      { key: 'traffic_sources', name: 'Traffic sources', included: features.includes('traffic_sources') },
      { key: 'ecommerce_reports', name: 'E-commerce reports', included: features.includes('ecommerce_reports') },
      { key: 'geographic_data', name: 'Geographic data', included: features.includes('geographic_data') },
      { key: 'pdf_export', name: 'PDF export', included: features.includes('pdf_export') },
      { key: 'realtime_analytics', name: 'Realtime analytics', included: false },
      { key: 'all_charts', name: 'All charts', included: false },
      { key: 'hourly_heatmap', name: 'Hourly heatmap', included: false },
    ];
  }

  // Pro and Agency plans
  return [
    ...baseFeatures,
    { key: 'all_charts', name: 'All charts', included: features.includes('all_charts') },
    { key: 'traffic_sources', name: 'Traffic sources', included: features.includes('traffic_sources') },
    { key: 'ecommerce_reports', name: 'E-commerce reports', included: features.includes('ecommerce_reports') },
    { key: 'realtime_analytics', name: 'Realtime analytics', included: features.includes('realtime_analytics') },
    { key: 'hourly_heatmap', name: 'Hourly heatmap', included: features.includes('hourly_heatmap') },
    { key: 'geographic_data', name: 'Geographic data', included: features.includes('geographic_data') },
    { key: 'pdf_export', name: 'PDF export', included: features.includes('pdf_export') },
  ];
}

function getSitesText(maxSites: number): string {
  if (maxSites === -1) return 'Unlimited sites';
  if (maxSites === 1) return '1 Webflow site';
  return `${maxSites} Webflow sites`;
}

function mapApiPlanToUI(plan: PlanData, currentPlanId: string | null): PricingPlan {
  const descriptions: Record<string, string> = {
    free: 'Perfect for getting started with analytics',
    starter: 'For small websites with basic needs',
    pro: 'For growing businesses and serious analytics',
    agency: 'For agencies managing multiple clients',
  };

  return {
    id: plan.id,
    name: plan.name.charAt(0).toUpperCase() + plan.name.slice(1),
    monthlyPrice: plan.monthly_price / 100, // Convert cents to dollars
    yearlyPrice: plan.yearly_price / 100,
    description: descriptions[plan.name] || '',
    features: buildPlanFeatures(plan),
    isCurrent: plan.id === currentPlanId,
    isPopular: plan.name === 'pro',
    color: plan.name === 'agency' ? '#1a1a1a' : '#2c6ecb',
  };
}

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'What happens when I upgrade?',
    answer:
      'When you upgrade, you immediately get access to all the features of your new plan. Your billing will be prorated based on the remaining time in your current billing period.',
  },
  {
    question: 'Can I downgrade later?',
    answer:
      'Yes, you can downgrade at any time. Your current plan features will remain active until the end of your billing period, then you will be moved to the lower plan.',
  },
  {
    question: 'How does billing work?',
    answer:
      'We bill monthly or yearly via credit card through Stripe. You can cancel anytime and your subscription will remain active until the end of the billing period.',
  },
  {
    question: 'Is there a free trial for paid plans?',
    answer:
      'We offer a 7-day free trial for the Pro plan. You can start your trial and get full access to all Pro features.',
  },
];

function BillingToggle({
  isYearly,
  onChange,
}: {
  isYearly: boolean;
  onChange: (yearly: boolean) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '8px 0',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '16px',
          padding: '8px 24px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e1e3e5',
        }}
      >
        <Text as="span" variant="bodyMd" fontWeight={!isYearly ? 'bold' : 'regular'}>
          <span style={{ color: !isYearly ? '#1a1a1a' : '#6b7280' }}>Monthly</span>
        </Text>
        <button
          onClick={() => onChange(!isYearly)}
          style={{
            width: '52px',
            height: '28px',
            borderRadius: '14px',
            backgroundColor: isYearly ? '#008060' : '#8c9196',
            border: 'none',
            cursor: 'pointer',
            position: 'relative',
            transition: 'background-color 0.2s ease',
            padding: 0,
          }}
        >
          <div
            style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              backgroundColor: 'white',
              position: 'absolute',
              top: '3px',
              left: isYearly ? '27px' : '3px',
              transition: 'left 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </button>
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" variant="bodyMd" fontWeight={isYearly ? 'bold' : 'regular'}>
            <span style={{ color: isYearly ? '#1a1a1a' : '#6b7280' }}>Yearly</span>
          </Text>
          <span
            style={{
              backgroundColor: '#b4fed3',
              color: '#0d5132',
              fontSize: '11px',
              fontWeight: 600,
              padding: '3px 8px',
              borderRadius: '10px',
            }}
          >
            Save 17%
          </span>
        </InlineStack>
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  isYearly,
  onSelect,
  isLoading,
}: {
  plan: PricingPlan;
  isYearly: boolean;
  onSelect: () => void;
  isLoading?: boolean;
}) {
  const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const period = plan.monthlyPrice === 0 ? 'forever' : isYearly ? 'year' : 'month';
  const monthlyEquivalent = isYearly && plan.yearlyPrice > 0 ? Math.round(plan.yearlyPrice / 12) : null;

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
      }}
    >
      {/* Popular badge */}
      {plan.isPopular && (
        <div
          style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: '#b4fed3',
            color: '#0d5132',
            fontSize: '12px',
            fontWeight: 600,
            padding: '4px 14px',
            borderRadius: '10px',
            whiteSpace: 'nowrap',
          }}
        >
          Most Popular
        </div>
      )}

      <div
        style={{
          height: '100%',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: plan.isPopular ? '2px solid #008060' : '1px solid #e1e3e5',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px' }}>
          <BlockStack gap="400">
            {/* Plan header */}
            <div>
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingMd" fontWeight="bold">
                  {plan.name}
                </Text>
                {plan.isCurrent && (
                  <span
                    style={{
                      backgroundColor: '#b4fed3',
                      color: '#0d5132',
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '4px 10px',
                      borderRadius: '10px',
                    }}
                  >
                    Current
                  </span>
                )}
              </InlineStack>
              <div style={{ marginTop: '4px' }}>
                <Text as="p" tone="subdued" variant="bodySm">
                  {plan.description}
                </Text>
              </div>
            </div>

            {/* Price section */}
            <div
              style={{
                padding: '16px 0',
                borderTop: '1px solid #e1e3e5',
                borderBottom: '1px solid #e1e3e5',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <span
                  style={{
                    fontSize: '36px',
                    fontWeight: 700,
                    color: '#1a1a1a',
                    lineHeight: 1,
                  }}
                >
                  ${price}
                </span>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>/{period}</span>
              </div>
              {monthlyEquivalent && (
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '13px',
                    color: '#008060',
                    fontWeight: 500,
                  }}
                >
                  ${monthlyEquivalent}/mo when billed yearly
                </div>
              )}
            </div>

            {/* Features */}
            <div
              style={{
                backgroundColor: '#f6f6f7',
                borderRadius: '8px',
                padding: '16px',
              }}
            >
              <BlockStack gap="300">
                {plan.features.map((feature, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: feature.included ? '#b4fed3' : '#e1e3e5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon
                        source={feature.included ? CheckIcon : XIcon}
                        tone={feature.included ? 'success' : 'subdued'}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: '13px',
                        color: feature.included ? '#202223' : '#8c9196',
                        textDecoration: feature.included ? 'none' : 'line-through',
                      }}
                    >
                      {feature.name}
                    </span>
                  </div>
                ))}
              </BlockStack>
            </div>

            {/* CTA Button */}
            <div style={{ marginTop: '8px' }}>
              {plan.isCurrent ? (
                <button
                  disabled
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    backgroundColor: '#f6f6f7',
                    color: '#8c9196',
                    border: '1px solid #e1e3e5',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'not-allowed',
                  }}
                >
                  Current Plan
                </button>
              ) : (
                <button
                  onClick={onSelect}
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    backgroundColor: plan.isPopular ? '#2c6ecb' : '#ffffff',
                    color: plan.isPopular ? '#ffffff' : '#202223',
                    border: plan.isPopular ? 'none' : '1px solid #e1e3e5',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: isLoading ? 'wait' : 'pointer',
                    transition: 'all 0.15s ease',
                    opacity: isLoading ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) {
                      if (plan.isPopular) {
                        e.currentTarget.style.backgroundColor = '#1f5199';
                      } else {
                        e.currentTarget.style.backgroundColor = '#f6f6f7';
                      }
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoading) {
                      if (plan.isPopular) {
                        e.currentTarget.style.backgroundColor = '#2c6ecb';
                      } else {
                        e.currentTarget.style.backgroundColor = '#ffffff';
                      }
                    }
                  }}
                >
                  {isLoading && <Spinner size="small" />}
                  {plan.monthlyPrice === 0 ? 'Get Started Free' : `Upgrade to ${plan.name}`}
                </button>
              )}
            </div>
          </BlockStack>
        </div>
      </div>
    </div>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e1e3e5',
      }}
    >
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd" fontWeight="bold">
          Frequently Asked Questions
        </Text>

        <BlockStack gap="200">
          {FAQ_ITEMS.map((item, index) => (
            <div
              key={index}
              style={{
                border: '1px solid #e1e3e5',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                style={{
                  display: 'flex',
                  width: '100%',
                  padding: '16px',
                  border: 'none',
                  backgroundColor: openIndex === index ? '#f6f6f7' : 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (openIndex !== index) {
                    e.currentTarget.style.backgroundColor = '#f6f6f7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (openIndex !== index) {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  {item.question}
                </Text>
                <span
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s ease',
                    marginLeft: '12px',
                  }}
                >
                  ▼
                </span>
              </button>
              {openIndex === index && (
                <div style={{ padding: '0 16px 16px 16px' }}>
                  <Text as="p" tone="subdued">
                    {item.answer}
                  </Text>
                </div>
              )}
            </div>
          ))}
        </BlockStack>
      </BlockStack>
    </div>
  );
}

export function Pricing() {
  const [searchParams] = useSearchParams();
  const [isYearly, setIsYearly] = useState(false);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check for checkout result from URL params
  useEffect(() => {
    const checkoutResult = searchParams.get('checkout');
    if (checkoutResult === 'success') {
      setSuccessMessage('Your subscription has been updated successfully!');
    } else if (checkoutResult === 'cancelled') {
      setError('Checkout was cancelled. You can try again whenever you\'re ready.');
    }
  }, [searchParams]);

  // Fetch plans and subscription on mount
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [plansRes, subRes] = await Promise.all([
          billingApi.getPlans(),
          billingApi.getSubscription(),
        ]);

        if (plansRes.success && plansRes.data) {
          const mappedPlans = plansRes.data.map((plan) =>
            mapApiPlanToUI(plan, subRes.data?.plan_id || null)
          );
          setPlans(mappedPlans);
        }

        if (subRes.success && subRes.data) {
          setSubscription(subRes.data);
        }
      } catch (err) {
        console.error('Failed to fetch billing data:', err);
        setError('Failed to load pricing information. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const handleSelectPlan = async (planId: string, planName: string) => {
    // Free plan - redirect to Stripe portal or show message
    if (planName.toLowerCase() === 'free') {
      // If subscription is already cancelling, show info message
      if (subscription?.cancel_at_period_end) {
        const endDate = subscription.current_period_end || subscription.trial_ends_at;
        alert(`Your subscription is already scheduled to be cancelled. You'll be automatically downgraded to the Free plan on ${endDate ? new Date(endDate).toLocaleDateString() : 'the end of your billing period'}.`);
        return;
      }
      // If user has an active paid subscription, redirect to portal to cancel
      if (subscription?.stripe_customer_id && subscription.plan_id !== 'plan_free') {
        try {
          const response = await billingApi.getPortalUrl();
          if (response.success && response.data.portal_url) {
            window.location.href = response.data.portal_url;
          }
        } catch (err) {
          console.error('Portal error:', err);
          setError('Failed to open subscription management. Please try again.');
        }
      }
      return;
    }

    setCheckoutLoading(planId);
    setError(null);

    try {
      const interval: BillingInterval = isYearly ? 'yearly' : 'monthly';
      const response = await billingApi.createCheckout(planId, interval);

      if (response.success && response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to start checkout. Please try again or contact support.');
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await billingApi.getPortalUrl();
      if (response.success && response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (err) {
      console.error('Portal error:', err);
      // Fallback to email
      window.location.href = 'mailto:care@analyzely.app?subject=Subscription Help';
    }
  };

  const currentPlanName = subscription?.plan?.name
    ? subscription.plan.name.charAt(0).toUpperCase() + subscription.plan.name.slice(1)
    : 'Free';

  const getStatusBadge = () => {
    if (!subscription) return { text: 'Active', color: '#b4fed3', textColor: '#0d5132' };

    // Check if subscription is scheduled for cancellation (via cancel_at_period_end or cancel_at)
    if (subscription.cancel_at_period_end && subscription.status === 'active') {
      return { text: 'Cancelling', color: '#ffc96b', textColor: '#594400' };
    }

    switch (subscription.status) {
      case 'trialing':
        return {
          text: `Trial (${subscription.trial_days_remaining || 0} days left)`,
          color: '#ffc96b',
          textColor: '#594400',
        };
      case 'past_due':
        return { text: 'Past Due', color: '#fed3d1', textColor: '#5c0a0a' };
      case 'cancelled':
        return { text: 'Cancelled', color: '#e1e3e5', textColor: '#6b7280' };
      default:
        return { text: 'Active', color: '#b4fed3', textColor: '#0d5132' };
    }
  };

  const statusBadge = getStatusBadge();

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Spinner size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <BlockStack gap="600">
        {/* Success/Error banners */}
        {successMessage && (
          <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
            {successMessage}
          </Banner>
        )}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        {/* Header */}
        <BlockStack gap="200">
          <Text as="h1" variant="headingLg" fontWeight="bold">
            Pricing Plans
          </Text>
          <Text as="p" tone="subdued">
            Choose the plan that best fits your needs
          </Text>
        </BlockStack>

        {/* Current Plan Banner */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '16px 20px',
            border: '1px solid #e1e3e5',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div>
            <Text as="span" variant="bodySm" tone="subdued">
              Your Current Plan
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
              <Text as="span" variant="headingMd" fontWeight="bold">
                {currentPlanName}
              </Text>
              <span
                style={{
                  backgroundColor: statusBadge.color,
                  color: statusBadge.textColor,
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: '10px',
                }}
              >
                {statusBadge.text}
              </span>
            </div>
            {subscription?.current_period_end && subscription.status !== 'cancelled' && (
              <Text as="p" tone={subscription.cancel_at_period_end ? 'caution' : 'subdued'} variant="bodySm">
                {subscription.status === 'trialing'
                  ? 'Trial ends'
                  : subscription.cancel_at_period_end
                    ? 'Cancels'
                    : 'Renews'} on{' '}
                {new Date(subscription.current_period_end).toLocaleDateString()}
              </Text>
            )}
          </div>
          {subscription?.stripe_customer_id && (
            <button
              onClick={handleManageSubscription}
              style={{
                padding: '10px 16px',
                backgroundColor: '#ffffff',
                border: '1px solid #e1e3e5',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f6f6f7';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              Manage Subscription
            </button>
          )}
        </div>

        {/* Billing Toggle */}
        <BillingToggle isYearly={isYearly} onChange={setIsYearly} />

        {/* Pricing Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            paddingTop: '12px',
          }}
        >
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isYearly={isYearly}
              onSelect={() => handleSelectPlan(plan.id, plan.name)}
              isLoading={checkoutLoading === plan.id}
            />
          ))}
        </div>

        {/* FAQ Section */}
        <FAQSection />

        {/* Contact Section */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '16px 20px',
            border: '1px solid #e1e3e5',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <BlockStack gap="100">
            <Text as="span" variant="headingSm" fontWeight="bold">
              Need a custom plan?
            </Text>
            <Text as="p" tone="subdued">
              Contact us for enterprise pricing and custom solutions
            </Text>
          </BlockStack>
          <button
            onClick={() =>
              (window.location.href = 'mailto:sales@analyzely.app?subject=Enterprise Inquiry')
            }
            style={{
              padding: '10px 16px',
              backgroundColor: '#1a1a1a',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#333333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#1a1a1a';
            }}
          >
            Contact Sales
          </button>
        </div>
      </BlockStack>
    </Layout>
  );
}
// Build timestamp: 1768966267
